import React, { useState } from "react";
import { Container, Grid, Form, Message, Icon } from "semantic-ui-react";
import { FormattedMessage, useIntl } from "react-intl";
import PrimaryButton from "../../components/Button/PrimaryButton";
import { Heading1, Heading2, Body1 } from "../../components/value-cssmodules/TextComponent";
import styles from "./Contact.module.scss";
import { LEGAL_CONFIG } from "../../config/legal";

const Contact = () => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Add actual email sending functionality
    console.log("Form submitted:", formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <div className={styles.contactPage}>
      <div className={styles.hero}>
        <Container>
          <Heading1 className={styles.heroTitle}>
            <FormattedMessage
              id="contact.title"
              defaultMessage="Get in Touch"
            />
          </Heading1>
          <Body1 className={styles.heroSubtitle}>
            <FormattedMessage
              id="contact.subtitle"
              defaultMessage="We'd love to hear from you. Send us a message and we'll respond as soon as possible."
            />
          </Body1>
        </Container>
      </div>

      <Container className={styles.container}>
        <Grid stackable columns={2}>
          <Grid.Column width={10}>
            <div className={styles.formSection}>
              <Heading2 className={styles.sectionTitle}>
                <FormattedMessage
                  id="contact.form.title"
                  defaultMessage="Send us a Message"
                />
              </Heading2>

              {submitted && (
                <Message positive icon>
                  <Icon name="check circle" />
                  <Message.Content>
                    <Message.Header>
                      <FormattedMessage
                        id="contact.form.success.title"
                        defaultMessage="Message Sent!"
                      />
                    </Message.Header>
                    <FormattedMessage
                      id="contact.form.success.message"
                      defaultMessage="Thank you for contacting us. We'll get back to you soon."
                    />
                  </Message.Content>
                </Message>
              )}

              <Form onSubmit={handleSubmit} className={styles.form}>
                <Form.Field required>
                  <label className={styles.label}>
                    <FormattedMessage
                      id="contact.form.name"
                      defaultMessage="Your Name"
                    />
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={intl.formatMessage({
                      id: "contact.form.name.placeholder",
                      defaultMessage: "John Doe",
                    })}
                    className={styles.input}
                  />
                </Form.Field>

                <Form.Field required>
                  <label className={styles.label}>
                    <FormattedMessage
                      id="contact.form.email"
                      defaultMessage="Email Address"
                    />
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={intl.formatMessage({
                      id: "contact.form.email.placeholder",
                      defaultMessage: "john@example.com",
                    })}
                    className={styles.input}
                  />
                </Form.Field>

                <Form.Field required>
                  <label className={styles.label}>
                    <FormattedMessage
                      id="contact.form.subject"
                      defaultMessage="Subject"
                    />
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder={intl.formatMessage({
                      id: "contact.form.subject.placeholder",
                      defaultMessage: "How can we help you?",
                    })}
                    className={styles.input}
                  />
                </Form.Field>

                <Form.Field required>
                  <label className={styles.label}>
                    <FormattedMessage
                      id="contact.form.message"
                      defaultMessage="Message"
                    />
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder={intl.formatMessage({
                      id: "contact.form.message.placeholder",
                      defaultMessage: "Tell us more about your inquiry...",
                    })}
                    className={styles.textarea}
                  />
                </Form.Field>

                <div className={styles.submitButton}>
                  <PrimaryButton
                    text={
                      <FormattedMessage
                        id="contact.form.submit"
                        defaultMessage="Send Message"
                      />
                    }
                    role="submit"
                  />
                </div>
              </Form>
            </div>
          </Grid.Column>

          <Grid.Column width={6}>
            <div className={styles.infoSection}>
              <Heading2 className={styles.sectionTitle}>
                <FormattedMessage
                  id="contact.info.title"
                  defaultMessage="Contact Information"
                />
              </Heading2>

              <div className={styles.infoCard}>
                <div className={styles.infoItem}>
                  <Icon name="mail" className={styles.infoIcon} />
                  <div>
                    <h3 className={styles.infoLabel}>
                      <FormattedMessage
                        id="contact.info.email.label"
                        defaultMessage="Email"
                      />
                    </h3>
                    <a
                      href={`mailto:${LEGAL_CONFIG.supportEmail}`}
                      className={styles.infoValue}
                    >
                      {LEGAL_CONFIG.supportEmail}
                    </a>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <Icon name="phone" className={styles.infoIcon} />
                  <div>
                    <h3 className={styles.infoLabel}>
                      <FormattedMessage
                        id="contact.info.phone.label"
                        defaultMessage="Phone"
                      />
                    </h3>
                    <a href="tel:+1234567890" className={styles.infoValue}>
                      +1 (234) 567-890
                    </a>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <Icon name="clock" className={styles.infoIcon} />
                  <div>
                    <h3 className={styles.infoLabel}>
                      <FormattedMessage
                        id="contact.info.hours.label"
                        defaultMessage="Business Hours"
                      />
                    </h3>
                    <p className={styles.infoValue}>
                      <FormattedMessage
                        id="contact.info.hours.value"
                        defaultMessage="Mon - Fri: 9:00 AM - 6:00 PM"
                      />
                    </p>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <Icon name="map marker alternate" className={styles.infoIcon} />
                  <div>
                    <h3 className={styles.infoLabel}>
                      <FormattedMessage
                        id="contact.info.location.label"
                        defaultMessage="Location"
                      />
                    </h3>
                    <p className={styles.infoValue}>
                      <FormattedMessage
                        id="contact.info.location.value"
                        defaultMessage="Warsaw, Poland"
                      />
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.socialSection}>
                <h3 className={styles.socialTitle}>
                  <FormattedMessage
                    id="contact.social.title"
                    defaultMessage="Follow Us"
                  />
                </h3>
                <div className={styles.socialIcons}>
                  <span className={styles.socialIcon}>
                    <Icon name="facebook" size="large" />
                  </span>
                  <span className={styles.socialIcon}>
                    <Icon name="instagram" size="large" />
                  </span>
                  <span className={styles.socialIcon}>
                    <Icon name="twitter" size="large" />
                  </span>
                  <span className={styles.socialIcon}>
                    <Icon name="pinterest" size="large" />
                  </span>
                </div>
              </div>
            </div>
          </Grid.Column>
        </Grid>
      </Container>
    </div>
  );
};

export default Contact;
